import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, serverTimestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import Papa from 'papaparse';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { generateUniqueSlug, ensureUniqueSlug } from '../utils/slugify';
import { parseMarkdownFile, composeLessonTitle } from '../utils/markdownCourseParser';

const CATEGORIES = [
  'Faith Foundations',
  'Life Questions',
  'Relationships',
  'Identity',
  'Purpose',
  'Suffering',
  'Marriage & Family',
  'Work & Calling',
  'Biblical Worldview',
  'Cultural Issues',
  'Apologetics',
  'Spiritual Growth',
  'Mental Health',
  'Prayer & Devotion',
  'Community & Church',
  'Mission & Evangelism'
];

const UploadContentResources = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [activeTab, setActiveTab] = useState('article'); // 'article' or 'video'
  const [, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  // Markdown course upload state
  const [mdItems, setMdItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [sharedFields, setSharedFields] = useState({ author: '', authorWebsite: '' });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(fileExtension)) {
      alert('Please upload a CSV or Excel file (.csv, .xls, .xlsx)');
      return;
    }

    setFile(selectedFile);
    setParsedData([]);
    setErrors([]);

    // Parse CSV file
    if (selectedFile.type === 'text/csv' || fileExtension === '.csv') {
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setErrors(results.errors.map(err => `Row ${err.row + 2}: ${err.message}`));
          }
          if (activeTab === 'article') {
            validateAndSetArticleData(results.data);
          } else {
            validateAndSetVideoData(results.data);
          }
        },
        error: (error) => {
          setErrors([`Error parsing CSV: ${error.message}`]);
        }
      });
    } else {
      // For Excel files, we'd need a library like xlsx
      alert('Excel file support coming soon. Please convert to CSV for now.');
    }
  };

  const validateAndSetArticleData = (data) => {
    const validated = [];
    const newErrors = [];

    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because header is row 1, and arrays are 0-indexed
      const rowErrors = [];

      // Required fields
      if (!row.title || !row.title.trim()) {
        rowErrors.push('Title is required');
      }
      if (!row.description || !row.description.trim()) {
        rowErrors.push('Description is required');
      }
      if (!row.articleContent || !row.articleContent.trim()) {
        rowErrors.push('Article Content is required');
      }

      if (rowErrors.length > 0) {
        newErrors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
        return;
      }

      // Parse arrays (comma-separated values)
      const parseArray = (value) => {
        if (!value) return [];
        return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
      };

      // Parse booleans
      const parseBoolean = (value) => {
        if (!value) return false;
        const lower = value.toString().toLowerCase().trim();
        return lower === 'true' || lower === 'yes' || lower === '1' || lower === 'y';
      };

      // Parse missional range
      const parseMissionalRange = (value, defaultVal) => {
        if (!value) return defaultVal;
        const num = parseInt(value);
        return isNaN(num) ? defaultVal : Math.max(-10, Math.min(9, num));
      };

      // Parse categories - validate against allowed categories
      const categories = parseArray(row.categories || '');
      const invalidCategories = categories.filter(cat => !CATEGORIES.includes(cat));
      if (invalidCategories.length > 0) {
        rowErrors.push(`Invalid categories: ${invalidCategories.join(', ')}`);
      }

      validated.push({
        title: row.title.trim(),
        description: row.description.trim(),
        articleContent: row.articleContent.trim(),
        author: (row.author || '').trim(),
        authorWebsite: (row.authorWebsite || '').trim(),
        duration: (row.duration || '').trim(),
        tags: parseArray(row.tags || ''),
        categories: categories.filter(cat => CATEGORIES.includes(cat)),
        keyTopics: parseArray(row.keyTopics || ''),
        targetAudience: {
          maturityLevel: {
            blue: parseBoolean(row.maturityLevelBlue),
            green: parseBoolean(row.maturityLevelGreen),
            yellow: parseBoolean(row.maturityLevelYellow)
          },
          relationshipContext: parseArray(row.relationshipContext || ''),
          lifeStage: parseArray(row.lifeStage || '')
        },
        contentDetails: {
          platform: (row.platform || '').trim(),
          language: (row.language || 'en').trim(),
          difficulty: (row.difficulty || 'beginner').trim(),
          format: (row.format || '').trim(),
          length: (row.length || '').trim()
        },
        missionalRange: {
          min: parseMissionalRange(row.missionalRangeMin, -10),
          max: parseMissionalRange(row.missionalRangeMax, 9)
        },
        relevantScaleLevels: parseArray(row.relevantScaleLevels || '').map(level => {
          const num = parseInt(level);
          return isNaN(num) ? null : num;
        }).filter(level => level !== null && level >= -10 && level <= 9),
        displayOrder: row.displayOrder ? parseInt(row.displayOrder) : 999
      });
    });

    if (newErrors.length > 0) {
      setErrors(prev => [...prev, ...newErrors]);
    }

    setParsedData(validated);
  };

  const validateAndSetVideoData = (data) => {
    const validated = [];
    const newErrors = [];

    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because header is row 1, and arrays are 0-indexed
      const rowErrors = [];

      // Required fields for videos
      if (!row.title || !row.title.trim()) {
        rowErrors.push('Title is required');
      }
      if (!row.description || !row.description.trim()) {
        rowErrors.push('Description is required');
      }
      if (!row.url || !row.url.trim()) {
        rowErrors.push('URL is required for videos');
      }

      if (rowErrors.length > 0) {
        newErrors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
        return;
      }

      // Parse arrays (comma-separated values)
      const parseArray = (value) => {
        if (!value) return [];
        return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
      };

      // Parse booleans
      const parseBoolean = (value) => {
        if (!value) return false;
        const lower = value.toString().toLowerCase().trim();
        return lower === 'true' || lower === 'yes' || lower === '1' || lower === 'y';
      };

      // Parse missional range
      const parseMissionalRange = (value, defaultVal) => {
        if (!value) return defaultVal;
        const num = parseInt(value);
        return isNaN(num) ? defaultVal : Math.max(-10, Math.min(9, num));
      };

      // Parse categories - validate against allowed categories
      const categories = parseArray(row.categories || '');
      const invalidCategories = categories.filter(cat => !CATEGORIES.includes(cat));
      if (invalidCategories.length > 0) {
        rowErrors.push(`Invalid categories: ${invalidCategories.join(', ')}`);
      }

      validated.push({
        title: row.title.trim(),
        description: row.description.trim(),
        type: 'video',
        url: row.url.trim(),
        transcription: (row.transcription || '').trim(),
        author: (row.author || '').trim(),
        authorWebsite: (row.authorWebsite || '').trim(),
        duration: (row.duration || '').trim(),
        tags: parseArray(row.tags || ''),
        categories: categories.filter(cat => CATEGORIES.includes(cat)),
        keyTopics: parseArray(row.keyTopics || ''),
        targetAudience: {
          maturityLevel: {
            blue: parseBoolean(row.maturityLevelBlue),
            green: parseBoolean(row.maturityLevelGreen),
            yellow: parseBoolean(row.maturityLevelYellow)
          },
          relationshipContext: parseArray(row.relationshipContext || ''),
          lifeStage: parseArray(row.lifeStage || '')
        },
        contentDetails: {
          platform: (row.platform || '').trim(),
          language: (row.language || 'en').trim(),
          difficulty: (row.difficulty || 'beginner').trim(),
          format: (row.format || '').trim(),
          length: (row.length || '').trim()
        },
        missionalRange: {
          min: parseMissionalRange(row.missionalRangeMin, -10),
          max: parseMissionalRange(row.missionalRangeMax, 9)
        },
        relevantScaleLevels: parseArray(row.relevantScaleLevels || '').map(level => {
          const num = parseInt(level);
          return isNaN(num) ? null : num;
        }).filter(level => level !== null && level >= -10 && level <= 9),
        displayOrder: row.displayOrder ? parseInt(row.displayOrder) : 999
      });
    });

    if (newErrors.length > 0) {
      setErrors(prev => [...prev, ...newErrors]);
    }

    setParsedData(validated);
  };

  const handleMarkdownFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => /\.(md|markdown)$/i.test(f.name));
    if (files.length === 0) {
      setErrors(['Please drop .md (Markdown) files.']);
      return;
    }
    setErrors([]);
    try {
      const parsed = await Promise.all(
        files.map(async (f) => parseMarkdownFile(f.name, await f.text()))
      );

      // Order by lesson number (files without a number fall to the end, alphabetically)
      parsed.sort((a, b) => {
        if (a.lessonNumber != null && b.lessonNumber != null) return a.lessonNumber - b.lessonNumber;
        if (a.lessonNumber != null) return -1;
        if (b.lessonNumber != null) return 1;
        return a.filename.localeCompare(b.filename);
      });

      const total = parsed.filter(p => p.lessonNumber != null).length;
      const items = parsed.map((p, idx) => ({
        ...p,
        id: `${p.filename}-${idx}`,
        title: composeLessonTitle({
          lessonName: p.lessonName,
          lessonNumber: p.lessonNumber,
          total,
          seriesTitle: p.seriesTitle,
        }),
      }));

      setMdItems(items);
    } catch (err) {
      console.error('Error parsing markdown files:', err);
      setErrors([`Error reading files: ${err.message}`]);
    }
  };

  const onDropMarkdown = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleMarkdownFiles(e.dataTransfer.files);
  };

  const updateMdItem = (id, field, value) => {
    setMdItems(prev => prev.map(it => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const removeMdItem = (id) => {
    setMdItems(prev => prev.filter(it => it.id !== id));
  };

  const handleMdDragEnd = (result) => {
    if (!result.destination) return;
    setMdItems(prev => {
      const items = Array.from(prev);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      return items;
    });
  };

  const applySharedToAll = () => {
    setMdItems(prev => prev.map(it => ({
      ...it,
      author: sharedFields.author || it.author,
      authorWebsite: sharedFields.authorWebsite || it.authorWebsite,
    })));
    setShowApplyModal(false);
  };

  const handleMarkdownUpload = async () => {
    if (mdItems.length === 0) {
      alert('No markdown files to upload. Please drop some .md files first.');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: mdItems.length });
    setSuccessCount(0);
    const uploadErrors = [];
    const usedSlugs = new Set();
    const orderOf = new Map(mdItems.map((it, idx) => [it.id, idx + 1]));

    const batchSize = 10;
    for (let i = 0; i < mdItems.length; i += batchSize) {
      const batch = writeBatch(db);
      const slice = mdItems.slice(i, i + batchSize);

      const prepared = [];
      for (const item of slice) {
        try {
          let slug = await ensureUniqueSlug(item.slug || item.title);
          while (slug && usedSlugs.has(slug)) {
            slug = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
          }
          if (slug) usedSlugs.add(slug);
          prepared.push({ slug, item });
        } catch (error) {
          console.error('Error generating slug:', error);
          uploadErrors.push(`Error preparing "${item.title}": ${error.message}`);
        }
      }

      for (const { slug, item } of prepared) {
        try {
          const resourceRef = doc(collection(db, 'contentTracks', trackId, 'resources'));
          const articleRef = doc(collection(db, 'articles'));
          const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
          const articleUrl = `${articleDomain}/article/${slug || articleRef.id}`;

          batch.set(articleRef, {
            title: item.title,
            slug: slug,
            content: item.html,
            description: item.description || '',
            subtitle: item.subtitle || '',
            author: item.author || '',
            authorName: item.author || '',
            authorWebsite: item.authorWebsite || '',
            website: item.authorWebsite || '',
            category: '',
            trackId: trackId,
            resourceId: resourceRef.id,
            createdAt: serverTimestamp(),
            createdBy: {
              userId: currentUser?.uid || '',
              name: userData?.name || '',
            },
          });

          batch.set(resourceRef, {
            title: item.title,
            description: item.description || '',
            type: 'article',
            url: articleUrl,
            status: 'published',
            articleDocId: articleRef.id,
            author: item.author || '',
            authorWebsite: item.authorWebsite || '',
            duration: '',
            tags: [],
            categories: [],
            keyTopics: [],
            targetAudience: {
              maturityLevel: { blue: false, green: false, yellow: false },
              relationshipContext: [],
              lifeStage: []
            },
            contentDetails: { platform: '', language: 'en', difficulty: 'beginner', format: 'article', length: '' },
            missionalRange: { min: -10, max: 9 },
            relevantScaleLevels: [],
            createdBy: {
              userId: currentUser?.uid || '',
              name: userData?.name || '',
              role: userData?.role || ''
            },
            dateCreated: serverTimestamp(),
            lastModified: serverTimestamp(),
            displayOrder: orderOf.get(item.id) || 999
          });
        } catch (error) {
          console.error('Error preparing markdown resource:', error);
          uploadErrors.push(`Error preparing "${item.title}": ${error.message}`);
        }
      }

      try {
        await batch.commit();
        setSuccessCount(prev => prev + prepared.length);
      } catch (error) {
        console.error('Error uploading batch:', error);
        uploadErrors.push(`Error uploading batch: ${error.message}`);
      }

      setUploadProgress({ current: Math.min(i + batchSize, mdItems.length), total: mdItems.length });
    }

    setUploading(false);

    if (uploadErrors.length > 0) {
      alert(`Upload completed with ${uploadErrors.length} error(s). Check console for details.`);
      console.error('Upload errors:', uploadErrors);
    } else {
      alert(`Successfully created ${mdItems.length} article(s)!`);
      navigate(`/content-tracks/${trackId}`);
    }
  };

  const handleUpload = async () => {
    if (parsedData.length === 0) {
      alert('No valid data to upload. Please check your file and try again.');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: parsedData.length });
    setSuccessCount(0);
    const uploadErrors = [];

    // Process in batches to avoid overwhelming Firestore
    const batchSize = 10;
    for (let i = 0; i < parsedData.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchData = parsedData.slice(i, i + batchSize);

      if (activeTab === 'article') {
        // Generate slugs first (needs async Firestore queries)
        const usedSlugs = new Set();
        const preparedItems = [];
        for (const resourceData of batchData) {
          try {
            let slug = await generateUniqueSlug(resourceData.title);
            while (usedSlugs.has(slug)) {
              slug = `${slug}-${Math.random().toString(36).slice(2, 6)}`;
            }
            usedSlugs.add(slug);
            preparedItems.push({ slug, resourceData });
          } catch (error) {
            console.error('Error generating slug:', error);
            uploadErrors.push(`Error preparing "${resourceData.title}": ${error.message}`);
          }
        }

        // Add both article and resource docs to the same batch (atomic)
        for (const { slug, resourceData } of preparedItems) {
          try {
            const resourceRef = doc(collection(db, 'contentTracks', trackId, 'resources'));
            const articleRef = doc(collection(db, 'articles'));
            const articleDomain = process.env.REACT_APP_ARTICLE_DOMAIN || 'https://tmrsrc.com';
            const articleUrl = `${articleDomain}/article/${slug || articleRef.id}`;

            batch.set(articleRef, {
              title: resourceData.title,
              slug: slug,
              content: resourceData.articleContent,
              description: resourceData.description || '',
              author: resourceData.author || '',
              authorName: resourceData.author || '',
              authorWebsite: resourceData.authorWebsite || '',
              website: resourceData.authorWebsite || '',
              category: (resourceData.categories && resourceData.categories[0]) || '',
              trackId: trackId,
              resourceId: resourceRef.id,
              createdAt: serverTimestamp(),
              createdBy: {
                userId: currentUser?.uid || '',
                name: userData?.name || '',
              },
            });

            batch.set(resourceRef, {
              title: resourceData.title,
              description: resourceData.description,
              type: 'article',
              url: articleUrl,
              status: 'published',
              articleDocId: articleRef.id,
              author: resourceData.author,
              authorWebsite: resourceData.authorWebsite,
              duration: resourceData.duration,
              tags: resourceData.tags,
              categories: resourceData.categories,
              keyTopics: resourceData.keyTopics,
              targetAudience: resourceData.targetAudience,
              contentDetails: resourceData.contentDetails,
              missionalRange: resourceData.missionalRange,
              relevantScaleLevels: resourceData.relevantScaleLevels,
              createdBy: {
                userId: currentUser?.uid || '',
                name: userData?.name || '',
                role: userData?.role || ''
              },
              dateCreated: serverTimestamp(),
              lastModified: serverTimestamp(),
              displayOrder: resourceData.displayOrder
            });
          } catch (error) {
            console.error('Error preparing resource:', error);
            uploadErrors.push(`Error preparing "${resourceData.title}": ${error.message}`);
          }
        }
      } else {
        // For videos, directly create resources
        for (const resourceData of batchData) {
          try {
            // Create resource document reference
            const resourceRef = doc(collection(db, 'contentTracks', trackId, 'resources'));
            batch.set(resourceRef, {
              title: resourceData.title,
              description: resourceData.description,
              type: 'video',
              url: resourceData.url,
              status: 'published',
              transcription: resourceData.transcription,
              author: resourceData.author,
              authorWebsite: resourceData.authorWebsite,
              duration: resourceData.duration,
              tags: resourceData.tags,
              categories: resourceData.categories,
              keyTopics: resourceData.keyTopics,
              targetAudience: resourceData.targetAudience,
              contentDetails: resourceData.contentDetails,
              missionalRange: resourceData.missionalRange,
              relevantScaleLevels: resourceData.relevantScaleLevels,
              createdBy: {
                userId: currentUser?.uid || '',
                name: userData?.name || '',
                role: userData?.role || ''
              },
              dateCreated: serverTimestamp(),
              lastModified: serverTimestamp(),
              displayOrder: resourceData.displayOrder
            });
          } catch (error) {
            console.error('Error preparing resource:', error);
            uploadErrors.push(`Error preparing "${resourceData.title}": ${error.message}`);
          }
        }
      }

      try {
        await batch.commit();
        setSuccessCount(prev => prev + batchData.length);
      } catch (error) {
        console.error('Error uploading batch:', error);
        uploadErrors.push(`Error uploading batch: ${error.message}`);
      }

      setUploadProgress({ current: Math.min(i + batchSize, parsedData.length), total: parsedData.length });
    }

    setUploading(false);

    if (uploadErrors.length > 0) {
      alert(`Upload completed with ${uploadErrors.length} error(s). Check console for details.`);
      console.error('Upload errors:', uploadErrors);
    } else {
      const resourceType = activeTab === 'article' ? 'article(s)' : 'video(s)';
      alert(`Successfully uploaded ${successCount} ${resourceType}!`);
      navigate(`/content-tracks/${trackId}`);
    }
  };

  const downloadTemplate = () => {
    let headers, exampleRow, filename;

    if (activeTab === 'article') {
      headers = [
        'title',
        'description',
        'articleContent',
        'author',
        'authorWebsite',
        'duration',
        'tags',
        'categories',
        'keyTopics',
        'maturityLevelBlue',
        'maturityLevelGreen',
        'maturityLevelYellow',
        'relationshipContext',
        'lifeStage',
        'platform',
        'language',
        'difficulty',
        'format',
        'length',
        'missionalRangeMin',
        'missionalRangeMax',
        'relevantScaleLevels',
        'displayOrder'
      ];

      exampleRow = [
        'Sample Article Title',
        'This is a sample description of the article.',
        'This is the full article content. You can paste multiple paragraphs here.',
        'Author Name',
        'https://authorwebsite.com',
        '5 min read',
        'tag1, tag2, tag3',
        'Faith Foundations, Spiritual Growth',
        'Topic 1, Topic 2, Topic 3',
        'true',
        'false',
        'true',
        'mentor-mentee, parent-child',
        'youth, young adult',
        'Website',
        'en',
        'beginner',
        'article',
        'short',
        '-10',
        '0',
        '-5, -4, -3',
        '1'
      ];
      filename = 'article-upload-template.csv';
    } else {
      headers = [
        'title',
        'description',
        'url',
        'transcription',
        'author',
        'authorWebsite',
        'duration',
        'tags',
        'categories',
        'keyTopics',
        'maturityLevelBlue',
        'maturityLevelGreen',
        'maturityLevelYellow',
        'relationshipContext',
        'lifeStage',
        'platform',
        'language',
        'difficulty',
        'format',
        'length',
        'missionalRangeMin',
        'missionalRangeMax',
        'relevantScaleLevels',
        'displayOrder'
      ];

      exampleRow = [
        'Sample Video Title',
        'This is a sample description of the video.',
        'https://www.youtube.com/watch?v=example',
        'Video transcription text goes here...',
        'Author Name',
        'https://authorwebsite.com',
        '15:30',
        'tag1, tag2, tag3',
        'Faith Foundations, Spiritual Growth',
        'Topic 1, Topic 2, Topic 3',
        'true',
        'false',
        'true',
        'mentor-mentee, parent-child',
        'youth, young adult',
        'YouTube',
        'en',
        'beginner',
        'sermon',
        'medium',
        '-10',
        '0',
        '-5, -4, -3',
        '1'
      ];
      filename = 'video-upload-template.csv';
    }

    const csv = [
      headers.join(','),
      exampleRow.map(val => `"${val.toString().replace(/"/g, '""')}"`).join(',')
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 md:p-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/content-tracks/${trackId}`)}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
          >
            ← Back to Track
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Upload Multiple Resources
          </h1>
          <p className="text-gray-600">
            {activeTab === 'markdown'
              ? 'Drag and drop Markdown (.md) lesson files to create articles and track resources in one step.'
              : 'Upload a CSV file with multiple resources to add them all at once to this content track.'}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('article');
                setFile(null);
                setParsedData([]);
                setMdItems([]);
                setErrors([]);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'article'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Article Upload
            </button>
            <button
              onClick={() => {
                setActiveTab('video');
                setFile(null);
                setParsedData([]);
                setMdItems([]);
                setErrors([]);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'video'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Video List Upload
            </button>
            <button
              onClick={() => {
                setActiveTab('markdown');
                setFile(null);
                setParsedData([]);
                setErrors([]);
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'markdown'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Markdown Files
            </button>
          </nav>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 space-y-6">
          {activeTab !== 'markdown' && (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Download the template CSV file below</li>
                  <li>Fill in the template with your {activeTab === 'article' ? 'article' : 'video'} data</li>
                  <li>Upload the completed CSV file</li>
                  <li>Review the parsed data and click "Upload All {activeTab === 'article' ? 'Articles' : 'Videos'}"</li>
                </ol>
              </div>

              {/* Template Download */}
              <div>
                <button
                  onClick={downloadTemplate}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Download CSV Template
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload CSV File
                </label>
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={handleFileChange}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE] transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: CSV, Excel (.csv, .xls, .xlsx)
                </p>
              </div>
            </>
          )}

          {/* Markdown Files */}
          {activeTab === 'markdown' && (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Drag and drop your .md lesson files (or choose them) all at once</li>
                  <li>Titles and descriptions are auto-generated. Edit them inline as needed</li>
                  <li>Drag rows to reorder. Use "Apply author to all" to set the author/website for every lesson</li>
                  <li>Click "Create Articles" to publish them all to this track</li>
                </ol>
              </div>

              {/* Dropzone */}
              <div
                onDrop={onDropMarkdown}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-[#0076BE] bg-blue-50' : 'border-gray-300'
                }`}
              >
                <p className="text-gray-700 font-medium mb-1">Drag &amp; drop .md files here</p>
                <p className="text-sm text-gray-500 mb-4">Drop your whole course of Markdown lessons at once</p>
                <label className="inline-block px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 cursor-pointer transition-colors">
                  Choose Files
                  <input
                    type="file"
                    accept=".md,.markdown"
                    multiple
                    onChange={(e) => handleMarkdownFiles(e.target.files)}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Parsed lessons list */}
              {mdItems.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      {mdItems.length} lesson{mdItems.length !== 1 ? 's' : ''} ready
                    </h3>
                    <button
                      onClick={() => setShowApplyModal(true)}
                      className="px-3 py-1.5 text-sm bg-[#0076BE] text-white rounded hover:bg-[#0065a3] transition-colors"
                    >
                      Apply author to all
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Drag rows to reorder. This order becomes the lesson sequence in the track.
                  </p>
                  <DragDropContext onDragEnd={handleMdDragEnd}>
                    <Droppable droppableId="md-list">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                          {mdItems.map((item, index) => (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                              {(prov, snapshot) => (
                                <div
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  className={`border rounded-lg p-4 bg-white ${
                                    snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500' : 'border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div
                                      {...prov.dragHandleProps}
                                      className="mt-2 text-gray-400 cursor-grab select-none"
                                      title="Drag to reorder"
                                    >
                                      ⠿
                                    </div>
                                    <div className="flex-shrink-0 mt-1 h-7 w-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                                      {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-2">
                                      <input
                                        value={item.title}
                                        onChange={(e) => updateMdItem(item.id, 'title', e.target.value)}
                                        className="w-full p-2 border border-gray-200 rounded font-medium focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE]"
                                      />
                                      <textarea
                                        value={item.description}
                                        onChange={(e) => updateMdItem(item.id, 'description', e.target.value)}
                                        rows={2}
                                        className="w-full p-2 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE]"
                                      />
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                                        <span className="font-mono">/article/{item.slug}</span>
                                        <span>{item.wordCount} words</span>
                                        {item.author && <span>· {item.author}</span>}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeMdItem(item.id)}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}
            </>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">Errors Found:</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-800 max-h-40 overflow-y-auto">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Parsed Data Preview */}
          {parsedData.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">
                Preview ({parsedData.length} {activeTab === 'article' ? 'article' : 'video'}{parsedData.length !== 1 ? 's' : ''} ready to upload)
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Title</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Author</th>
                        {activeTab === 'video' && (
                          <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">URL</th>
                        )}
                        <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b">Categories</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((row, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-2">{row.title}</td>
                          <td className="px-4 py-2">{row.author || '-'}</td>
                          {activeTab === 'video' && (
                            <td className="px-4 py-2 text-xs text-blue-600 truncate max-w-xs">{row.url || '-'}</td>
                          )}
                          <td className="px-4 py-2">{row.categories.join(', ') || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-blue-900">Uploading...</span>
                <span className="text-sm text-blue-700">
                  {uploadProgress.current} / {uploadProgress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Button (CSV) */}
          {activeTab !== 'markdown' && parsedData.length > 0 && !uploading && (
            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                className="px-8 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#0065a3] transition-colors shadow-md"
              >
                Upload All {activeTab === 'article' ? 'Articles' : 'Videos'} ({parsedData.length})
              </button>
            </div>
          )}

          {/* Create Button (Markdown) */}
          {activeTab === 'markdown' && mdItems.length > 0 && !uploading && (
            <div className="flex justify-end">
              <button
                onClick={handleMarkdownUpload}
                className="px-8 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#0065a3] transition-colors shadow-md"
              >
                Create {mdItems.length} Article{mdItems.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>

        {/* Spreadsheet Format Documentation */}
        {activeTab !== 'markdown' && (
        <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">CSV Format Guide</h2>
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Required Fields:</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>title*</strong> - {activeTab === 'article' ? 'Article' : 'Video'} title</li>
                <li><strong>description*</strong> - Brief description</li>
                {activeTab === 'article' ? (
                  <li><strong>articleContent*</strong> - Full article text content</li>
                ) : (
                  <li><strong>url*</strong> - Video URL (e.g., YouTube, Vimeo, etc.)</li>
                )}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Optional Fields:</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li><strong>author</strong> - Author name</li>
                <li><strong>authorWebsite</strong> - Author website URL</li>
                <li><strong>duration</strong> - {activeTab === 'article' ? 'Reading time (e.g., "5 min read")' : 'Video duration (e.g., "15:30")'}</li>
                {activeTab === 'video' && (
                  <li><strong>transcription</strong> - Video transcription text (optional but recommended)</li>
                )}
                <li><strong>tags</strong> - Comma-separated tags (e.g., "tag1, tag2, tag3")</li>
                <li><strong>categories</strong> - Comma-separated categories from: {CATEGORIES.join(', ')}</li>
                <li><strong>keyTopics</strong> - Comma-separated key topics</li>
                <li><strong>maturityLevelBlue</strong> - true/false</li>
                <li><strong>maturityLevelGreen</strong> - true/false</li>
                <li><strong>maturityLevelYellow</strong> - true/false</li>
                <li><strong>relationshipContext</strong> - Comma-separated: parent-child, mentor-mentee, marriage</li>
                <li><strong>lifeStage</strong> - Comma-separated: youth, young adult, parent, senior</li>
                <li><strong>platform</strong> - Content platform</li>
                <li><strong>language</strong> - Language code (default: "en")</li>
                <li><strong>difficulty</strong> - beginner, intermediate, or advanced</li>
                <li><strong>format</strong> - Content format</li>
                <li><strong>length</strong> - short, medium, or long</li>
                <li><strong>missionalRangeMin</strong> - Number from -10 to 9</li>
                <li><strong>missionalRangeMax</strong> - Number from -10 to 9</li>
                <li><strong>relevantScaleLevels</strong> - Comma-separated numbers (e.g., "-5, -4, -3")</li>
                <li><strong>displayOrder</strong> - Number for ordering (default: 999)</li>
              </ul>
            </div>
          </div>
        </div>
        )}

        {/* Markdown Format Guide */}
        {activeTab === 'markdown' && (
          <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Markdown Format Guide</h2>
            <div className="space-y-4 text-sm text-gray-700">
              <p>
                Drop a folder of <strong>.md</strong> files. Each file becomes a published article and a resource in this track.
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>URL slug</strong> comes from the filename, e.g. <span className="font-mono">Life-with-God-Lesson-01.md</span> &rarr; <span className="font-mono">/article/life-with-god-lesson-01</span></li>
                <li><strong>Order</strong> is read from the lesson number in the filename (you can still drag to reorder)</li>
                <li><strong>Title</strong> is built as <em>Lesson Name - Lesson N of Total - Series</em>, using the first <span className="font-mono"># H1</span> (series) and <span className="font-mono">## H2</span> (lesson). Editable per row.</li>
                <li><strong>Description</strong> is auto-generated from the first paragraph. Editable per row.</li>
                <li><strong>Author / Website</strong> can be set for every lesson at once with "Apply author to all".</li>
                <li>Optional YAML frontmatter (<span className="font-mono">title</span>, <span className="font-mono">description</span>, <span className="font-mono">author</span>, <span className="font-mono">authorWebsite</span>) is respected if present.</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Apply-to-all modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Apply to all lessons</h3>
            <p className="text-sm text-gray-500 mb-4">
              These values will be set on every lesson in the list. Leave a field blank to keep existing values.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                <input
                  value={sharedFields.author}
                  onChange={(e) => setSharedFields(prev => ({ ...prev, author: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE]"
                  placeholder="Author name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author Website</label>
                <input
                  value={sharedFields.authorWebsite}
                  onChange={(e) => setSharedFields(prev => ({ ...prev, authorWebsite: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#0076BE] focus:border-[#0076BE]"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowApplyModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={applySharedToAll}
                className="px-4 py-2 bg-[#0076BE] text-white rounded hover:bg-[#0065a3] font-medium"
              >
                Apply to all
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UploadContentResources;
